import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import { ArrowLeft, Save, User, Star, Crown, Sparkles, ChevronRight, AtSign, Check, X as XIcon, Loader2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useAuth } from '@/hooks/useAuth';
import { useProgress } from '@/hooks/useProgress';
import { usePremium } from '@/hooks/usePremium';
import { usePreferredSport } from '@/hooks/usePreferredSport';
import { useSportSkillLevel } from '@/hooks/useSportSkillLevel';
import { supabase } from '@/integrations/supabase/client';
import { validateUsernameFormat, checkUsernameAvailable, sanitizeUsername } from '@/lib/usernameValidation';
import { toast } from 'sonner';
import { StreakFlame } from '@/components/StreakFlame';
import { XPBar } from '@/components/XPBar';
import { AchievementsGrid } from '@/components/AchievementsGrid';
import { getLevelName, getNextLevelXp, getCurrentLevelXp } from '@/hooks/useProgress';
import { FaqSection, type FaqItem } from '@/components/FaqSection';
import { ChangePasswordCard } from '@/components/ChangePasswordCard';
import { ActivateCoachCard } from '@/components/profile/ActivateCoachCard';
import { ChildAccountsCard } from '@/components/profile/ChildAccountsCard';
import { ProfileRankAndGems } from '@/components/profile/ProfileRankAndGems';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { RecentInvoices } from '@/components/RecentInvoices';

const NFL_TEAMS = [
  'Arizona Cardinals', 'Atlanta Falcons', 'Baltimore Ravens', 'Buffalo Bills',
  'Carolina Panthers', 'Chicago Bears', 'Cincinnati Bengals', 'Cleveland Browns',
  'Dallas Cowboys', 'Denver Broncos', 'Detroit Lions', 'Green Bay Packers',
  'Houston Texans', 'Indianapolis Colts', 'Jacksonville Jaguars', 'Kansas City Chiefs',
  'Las Vegas Raiders', 'Los Angeles Chargers', 'Los Angeles Rams', 'Miami Dolphins',
  'Minnesota Vikings', 'New England Patriots', 'New Orleans Saints', 'New York Giants',
  'New York Jets', 'Philadelphia Eagles', 'Pittsburgh Steelers', 'San Francisco 49ers',
  'Seattle Seahawks', 'Tampa Bay Buccaneers', 'Tennessee Titans', 'Washington Commanders',
];

const SOCCER_CLUBS = [
  'Arsenal', 'Aston Villa', 'Barcelona', 'Bayern Munich', 'Borussia Dortmund',
  'Chelsea', 'Inter Milan', 'Juventus', 'Liverpool', 'Manchester City',
  'Manchester United', 'Napoli', 'Newcastle United', 'Paris Saint-Germain',
  'Real Madrid', 'Tottenham Hotspur', 'AC Milan', 'Atletico Madrid',
  'Bayer Leverkusen', 'Benfica', 'Brighton', 'RB Leipzig',
  'Sporting CP', 'West Ham United',
];

const LEVEL_OPTIONS = [
  { value: 'beginner', label: '🌱 Rookie', desc: 'New to the sport' },
  { value: 'intermediate', label: '⭐ Varsity', desc: 'Know the basics well' },
  { value: 'advanced', label: '🧠 Pro', desc: 'Deep understanding' },
] as const;

const SPORTS = [
  { key: 'football', emoji: '🏈', label: 'Football' },
  { key: 'soccer', emoji: '⚽', label: 'Soccer' },
  { key: 'basketball', emoji: '🏀', label: 'Basketball' },
  { key: 'baseball', emoji: '⚾', label: 'Baseball' },
] as const;

function SportLevelsSection({ getLevel, setSkillLevel }: { getLevel: (s: string) => string | null; setSkillLevel: (s: string, l: any) => Promise<void> }) {
  const [drawerSport, setDrawerSport] = useState<string | null>(null);

  const handleSelect = async (level: string) => {
    if (!drawerSport) return;
    await setSkillLevel(drawerSport, level as any);
    const sportLabel = SPORTS.find(s => s.key === drawerSport)?.label ?? drawerSport;
    const levelLabel = LEVEL_OPTIONS.find(l => l.value === level)?.label ?? level;
    toast.success(`Your ${sportLabel} lessons have been updated to ${levelLabel}`);
    setDrawerSport(null);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        className="bg-card rounded-2xl border-2 border-border p-5 shadow-card space-y-3"
      >
        <h3 className="font-display font-bold text-lg">Sport Levels</h3>
        <p className="text-xs text-muted-foreground">Controls which lessons are highlighted for you</p>
        {SPORTS.map(sport => {
          const level = getLevel(sport.key);
          const levelLabel = LEVEL_OPTIONS.find(l => l.value === level)?.label ?? 'Not set';
          return (
            <button
              key={sport.key}
              onClick={() => setDrawerSport(sport.key)}
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-muted/50 transition-colors text-left"
            >
              <span className="text-lg">{sport.emoji}</span>
              <span className="text-sm font-display font-bold flex-1">{sport.label}</span>
              <span className="text-xs text-muted-foreground">{levelLabel}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          );
        })}
      </motion.div>

      <Drawer open={!!drawerSport} onOpenChange={open => !open && setDrawerSport(null)}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-display font-bold">
              {SPORTS.find(s => s.key === drawerSport)?.emoji} Change {SPORTS.find(s => s.key === drawerSport)?.label} Level
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-2">
            {LEVEL_OPTIONS.map(opt => {
              const isActive = getLevel(drawerSport ?? '') === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                    isActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                  }`}
                >
                  <span className="text-lg">{opt.label.split(' ')[0]}</span>
                  <div className="flex-1">
                    <p className="text-sm font-display font-bold">{opt.label.split(' ').slice(1).join(' ')}</p>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                  {isActive && <span className="text-xs font-display font-bold text-primary">Current</span>}
                </button>
              );
            })}
            <p className="text-[10px] text-muted-foreground text-center pt-2">Changing level won't reset your progress</p>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function JoinTeamSection() {
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [myTeams, setMyTeams] = useState<{ id: string; name: string; sport: string }[]>([]);

  useEffect(() => {
    supabase
      .from('team_members')
      .select('team_id, teams(id, name, sport)')
      .then(({ data }) => {
        if (data) {
          setMyTeams(
            data
              .filter((d: any) => d.teams)
              .map((d: any) => ({ id: d.teams.id, name: d.teams.name, sport: d.teams.sport }))
          );
        }
      });
  }, []);

  const handleJoin = async () => {
    const code = joinCode.trim().toLowerCase();
    if (!code || code.length < 4) {
      toast.error('Please enter a valid join code');
      return;
    }
    setJoining(true);
    const { data, error } = await supabase.rpc('join_team_by_code', { p_join_code: code });
    setJoining(false);
    if (error) {
      toast.error('Failed to join team');
      return;
    }
    const result = data as any;
    if (result?.error) {
      toast.error(result.error);
    } else {
      toast.success(`Joined ${result.team_name}! 🎉`);
      setJoinCode('');
      // Refresh teams
      const { data: updated } = await supabase
        .from('team_members')
        .select('team_id, teams(id, name, sport)');
      if (updated) {
        setMyTeams(
          updated
            .filter((d: any) => d.teams)
            .map((d: any) => ({ id: d.teams.id, name: d.teams.name, sport: d.teams.sport }))
        );
      }
    }
  };

  const sportEmoji = (sport: string) =>
    sport === 'basketball' ? '🏀' : sport === 'soccer' ? '⚽' : '🏈';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.13 }}
      className="bg-card rounded-2xl border-2 border-border p-5 shadow-card space-y-3"
    >
      <h3 className="font-display font-bold text-lg flex items-center gap-2">
        <Users className="w-5 h-5 text-primary" /> My Teams
      </h3>

      {myTeams.length > 0 && (
        <div className="space-y-1.5">
          {myTeams.map(team => (
            <div key={team.id} className="flex items-center gap-2 p-2 rounded-xl bg-muted/50">
              <span className="text-lg">{sportEmoji(team.sport)}</span>
              <span className="text-sm font-display font-bold flex-1">{team.name}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          value={joinCode}
          onChange={e => setJoinCode(e.target.value.slice(0, 10))}
          placeholder="Enter join code"
          className="h-10 rounded-xl border-2 font-mono text-sm"
          onKeyDown={e => e.key === 'Enter' && handleJoin()}
        />
        <Button
          onClick={handleJoin}
          disabled={joining || !joinCode.trim()}
          size="sm"
          className="font-display font-bold px-4"
        >
          {joining ? 'Joining...' : 'Join'}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">Ask your coach for a team join code</p>
    </motion.div>
  );
}

interface ProfilePageProps {
  onBack?: () => void;
}

export default function Profile({ onBack }: ProfilePageProps) {
  const { user, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const { progress, accuracy } = useProgress();
  const { isPremium, setShowUpgrade, openCustomerPortal, plan, subscriptionEnd } = usePremium();
  const { preferredSport } = usePreferredSport();
  const { getLevel, setLevel: setSkillLevel } = useSportSkillLevel();
  const { isPlayer, isParent, playerProfile } = usePlayerProfiles();
  const [displayName, setDisplayName] = useState('');
  const [username, setUsernameVal] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const usernameDebounce = useRef<ReturnType<typeof setTimeout>>();
  const [favoriteTeam, setFavoriteTeam] = useState('');
  const [knowledgeLevel, setKnowledgeLevel] = useState('');
  const [learningGoals, setLearningGoals] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [followingCount, setFollowingCount] = useState(0);
  const [followersCount, setFollowersCount] = useState(0);

  const fetchFollowCounts = useCallback(async () => {
    if (!user) return;
    const [{ count: following }, { count: followers }] = await Promise.all([
      supabase.from('user_follows').select('*', { count: 'exact', head: true }).eq('follower_id', user.id),
      supabase.from('user_follows').select('*', { count: 'exact', head: true }).eq('following_id', user.id),
    ]);
    setFollowingCount(following ?? 0);
    setFollowersCount(followers ?? 0);
  }, [user]);

  useEffect(() => { fetchFollowCounts(); }, [fetchFollowCounts]);

  // Username validation effect
  useEffect(() => {
    if (!username || username === originalUsername) {
      setUsernameError('');
      setUsernameAvailable(username === originalUsername ? null : null);
      return;
    }
    const format = validateUsernameFormat(username, displayName);
    if (!format.valid) {
      setUsernameError(format.error!);
      setUsernameAvailable(null);
      return;
    }
    setUsernameError('');
    setUsernameAvailable(null);
    setCheckingUsername(true);

    if (usernameDebounce.current) clearTimeout(usernameDebounce.current);
    usernameDebounce.current = setTimeout(async () => {
      const available = await checkUsernameAvailable(username);
      setUsernameAvailable(available);
      if (!available) setUsernameError('Username is already taken');
      setCheckingUsername(false);
    }, 500);

    return () => { if (usernameDebounce.current) clearTimeout(usernameDebounce.current); };
  }, [username, displayName, originalUsername]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('display_name, favorite_team, knowledge_level, learning_goals, username')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setDisplayName(data.display_name ?? '');
          setFavoriteTeam(data.favorite_team ?? '');
          setKnowledgeLevel((data as any).knowledge_level ?? '');
          setLearningGoals((data as any).learning_goals ?? []);
          setUsernameVal((data as any).username ?? '');
          setOriginalUsername((data as any).username ?? '');
        }
        setLoaded(true);
      });
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    const trimmedName = displayName.trim().slice(0, 100);
    const trimmedUsername = username.trim();

    // Username is required
    if (!trimmedUsername) {
      toast.error('Username is required');
      return;
    }

    // Validate username if changed
    if (trimmedUsername !== originalUsername) {
      const formatCheck = validateUsernameFormat(trimmedUsername, trimmedName);
      if (!formatCheck.valid) {
        toast.error(formatCheck.error!);
        return;
      }
      const available = await checkUsernameAvailable(trimmedUsername);
      if (!available) {
        toast.error('Username is already taken');
        return;
      }
    }

    setSaving(true);
    const updateData: Record<string, any> = {
      display_name: trimmedName || null,
      favorite_team: favoriteTeam || null,
    };
    if (trimmedUsername !== originalUsername) {
      updateData.username = trimmedUsername;
    }
    const { error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('user_id', user.id);
    setSaving(false);
    if (error) {
      if (error.message?.includes('unique') || error.code === '23505') {
        toast.error('Username is already taken');
      } else {
        toast.error('Failed to save profile');
      }
    } else {
      setOriginalUsername(trimmedUsername);
      toast.success('Profile updated!');
    }
  };

  const levelName = getLevelName(progress.level);
  const nextXp = getNextLevelXp(progress.level);
  const currentXp = getCurrentLevelXp(progress.level);

  return (
    <div>
      {onBack && (
        <header className="sticky top-0 z-10 bg-secondary text-secondary-foreground">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
            <button onClick={onBack} className="text-secondary-foreground/70 hover:text-secondary-foreground transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-display font-extrabold text-xl italic tracking-tight">Profile</h1>
          </div>
        </header>
      )}

      <div className="space-y-6">
        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-2xl border-2 border-border p-5 shadow-card text-center"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <User className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-display font-extrabold text-xl">{username || user?.email}</h2>
          {favoriteTeam && (
            <p className="text-sm text-muted-foreground mt-0.5 flex items-center justify-center gap-1">
              <Star className="w-3.5 h-3.5 text-accent" /> {favoriteTeam}
            </p>
          )}
          {knowledgeLevel && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
              {knowledgeLevel === 'beginner' ? '🌱 Rookie' : knowledgeLevel === 'intermediate' ? '⭐ Fan' : '🧠 Student of the Game'}
            </p>
          )}
          {learningGoals.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center mt-2">
              {learningGoals.map(g => {
                const label = g.startsWith('custom:') ? g.slice(7) : g === 'watch-smarter' ? '📺 Watch Smarter' : g === 'talk-the-talk' ? '💬 Talk the Talk' : '📋 Coach/Play';
                return (
                  <span key={g} className="text-[10px] font-display font-bold bg-muted px-2 py-0.5 rounded-full">{label}</span>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div>
              <StreakFlame streak={progress.streak} size="sm" />
            </div>
            <div>
              <p className="display-number text-lg text-primary">{progress.xp.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total XP</p>
            </div>
            <div>
              <p className="display-number text-lg text-warning">{accuracy}%</p>
              <p className="text-xs text-muted-foreground">Accuracy</p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-border/50">
            <div className="text-center">
              <p className="display-number text-lg">{followingCount}</p>
              <p className="text-xs text-muted-foreground">Following</p>
            </div>
            <div className="w-px h-8 bg-border/50" />
            <div className="text-center">
              <p className="display-number text-lg">{followersCount}</p>
              <p className="text-xs text-muted-foreground">Followers</p>
            </div>
          </div>
          <div className="mt-4">
            <ProfileRankAndGems />
          </div>
          <div className="mt-4">
            <XPBar current={progress.xp} levelMin={currentXp} levelMax={nextXp} levelName={levelName} />
          </div>
        </motion.div>


        {/* Manage Billing — hidden for child player accounts */}
        {!isPlayer && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-card rounded-2xl border-2 border-border p-5 shadow-card"
        >
          {isPremium ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  Manage Billing
                </h3>
                {plan === 'founding' && (
                  <span className="text-[10px] font-display font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-yellow-400 to-amber-600 text-white">
                    FOUNDING
                  </span>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-sm font-body text-foreground">
                  <span className="text-muted-foreground">Plan:</span>{' '}
                  <span className="font-display font-bold">
                    {plan === 'founding' ? 'Founding Member' : plan === 'annual' ? 'Annual' : plan === 'coach_pro' || plan === 'coach' ? 'Coach Pro' : 'Monthly'}
                  </span>
                </p>
                {subscriptionEnd && (
                  <p className="text-sm font-body text-foreground">
                    <span className="text-muted-foreground">Next renewal:</span>{' '}
                    <span className="font-display font-bold">
                      {new Date(subscriptionEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </p>
                )}
                {plan === 'founding' && (
                  <p className="text-xs font-display font-bold text-amber-600">
                    🔒 Locked in at $3.99/month forever
                  </p>
                )}
              </div>

              <Button
                onClick={openCustomerPortal}
                variant="outline"
                className="w-full h-11 text-sm font-display font-bold rounded-xl border-2"
              >
                Open Billing Portal
              </Button>

              <RecentInvoices />
            </div>

          ) : (
            <div className="text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
            <div>
                <h3 className="font-display font-bold text-lg">Free Plan</h3>
                <p className="text-sm text-muted-foreground">
                  Unlock all sports, advanced lessons & playbooks
                </p>
              </div>
              <Button
                onClick={() => setShowUpgrade(true)}
                className="w-full h-10 text-sm font-display font-bold rounded-xl"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </Button>
            </div>
          )}
        </motion.div>
        )}


        {/* Edit Profile — primary/parent accounts only. Child players can't change username, display name, or favorite team. */}
        {!isPlayer && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-2xl border-2 border-border p-5 shadow-card space-y-4"
        >
          <h3 className="font-display font-bold text-lg">Edit Profile</h3>

          <div>
            <label className="text-sm font-body font-medium text-muted-foreground mb-1.5 block">
              Display Name
            </label>
            <Input
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Coach McScheme"
              maxLength={100}
              className="h-12 rounded-xl border-2 font-body"
            />
          </div>

          <div>
            <label className="text-sm font-body font-medium text-muted-foreground mb-1.5 block">
              Username
            </label>
            <div className="relative">
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={username}
                onChange={e => setUsernameVal(sanitizeUsername(e.target.value))}
                placeholder="your_username"
                maxLength={20}
                className={`pl-10 pr-10 h-12 rounded-xl border-2 font-body ${
                  usernameError ? 'border-destructive' : usernameAvailable === true && username !== originalUsername ? 'border-green-500' : ''
                }`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checkingUsername && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
                {!checkingUsername && usernameAvailable === true && username !== originalUsername && <Check className="w-4 h-4 text-green-500" />}
                {!checkingUsername && usernameError && <XIcon className="w-4 h-4 text-destructive" />}
              </div>
            </div>
            {usernameError && (
              <p className="text-xs text-destructive font-body mt-1 pl-1">{usernameError}</p>
            )}
            {!usernameError && usernameAvailable === true && username !== originalUsername && (
              <p className="text-xs text-green-600 font-body mt-1 pl-1">Username available ✓</p>
            )}
          </div>

          <div>
            <label className="text-sm font-body font-medium text-muted-foreground mb-1.5 block">
              {preferredSport === 'soccer' ? 'Favorite Club' : 'Favorite NFL Team'}
            </label>
            <select
              value={favoriteTeam}
              onChange={e => setFavoriteTeam(e.target.value)}
              className="w-full h-12 rounded-xl border-2 border-input bg-background px-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select a team...</option>
              {(preferredSport === 'soccer' ? SOCCER_CLUBS : NFL_TEAMS).map(team => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || !loaded}
            className="w-full h-12 text-base font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </motion.div>
        )}

        {/* Child player read-only profile view */}
        {isPlayer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl border-2 border-border p-5 shadow-card space-y-4"
          >
            <h3 className="font-display font-bold text-lg">Your Profile</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40">
                <span className="text-xs font-body text-muted-foreground">Username</span>
                <span className="text-sm font-display font-bold">@{username || '—'}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40">
                <span className="text-xs font-body text-muted-foreground">Sport</span>
                <span className="text-sm font-display font-bold capitalize">
                  {playerProfile?.sport ? `${SPORTS.find(s => s.key === playerProfile.sport)?.emoji ?? ''} ${playerProfile.sport}` : '—'}
                </span>
              </div>
              {playerProfile?.skill_level && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40">
                  <span className="text-xs font-body text-muted-foreground">Level</span>
                  <span className="text-sm font-display font-bold">
                    {LEVEL_OPTIONS.find(l => l.value === playerProfile.skill_level)?.label ?? playerProfile.skill_level}
                  </span>
                </div>
              )}
              {playerProfile?.position && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40">
                  <span className="text-xs font-body text-muted-foreground">Position</span>
                  <span className="text-sm font-display font-bold">{playerProfile.position}</span>
                </div>
              )}
            </div>
            <div className="rounded-xl bg-primary/5 border border-primary/20 p-3">
              <p className="text-xs font-body text-muted-foreground leading-relaxed">
                Ask your parent or guardian to update your username, sport, or level from their account.
              </p>
            </div>
          </motion.div>
        )}

        {/* Skill Level Per Sport — primary/parent accounts only */}
        {!isPlayer && <SportLevelsSection getLevel={getLevel} setSkillLevel={setSkillLevel} />}

        {/* Join / View Teams — primary/parent accounts only; teams are assigned by the parent for child players */}
        {/* Parents never join teams themselves — they add their child from the Parent Dashboard */}
        {!isPlayer && !isParent && <JoinTeamSection />}

        {/* Become a Coach / Child Accounts — primary accounts only */}
        {!isPlayer && (
          <>
            <ActivateCoachCard />
            <ChildAccountsCard />
          </>
        )}

        {/* Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-card rounded-2xl border-2 border-border p-5 shadow-card"
        >
          <AchievementsGrid />
        </motion.div>

        {/* FAQ / Help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.17 }}
          className="bg-card rounded-2xl border-2 border-border p-5 shadow-card"
        >
          <FaqSection
            items={[
              { id: 'how-xp', question: 'How do I earn XP?', answer: 'Complete lessons to earn XP. Each lesson awards XP based on how many questions you answer correctly. Bonus XP is awarded for perfect scores and maintaining streaks.' },
              { id: 'streaks', question: 'What are streaks?', answer: 'Your streak counts consecutive days you\'ve completed at least one lesson. Hit milestone streaks (3, 7, 14, 30 days) to earn bonus XP and gems. Missing a day resets your streak to zero!' },
              { id: 'hearts', question: 'What are hearts and how do I get more?', answer: 'Hearts are your "lives" — you lose one each time you answer incorrectly during a lesson. If you run out, you\'ll need to wait for them to refill or use gems. Pro members get unlimited hearts.' },
              { id: 'skill-level', question: 'How does the skill level setting work?', answer: 'Your skill level (Rookie, Varsity, Pro) for each sport controls which lessons are highlighted for you. Changing your level won\'t reset any progress — it just adjusts the difficulty of recommended content.' },
              { id: 'join-team', question: 'How do I join a team?', answer: 'Ask your coach for a join code, then enter it in the "My Teams" section above. Once joined, your coach can assign lessons and track your progress.' },
              { id: 'assigned-lessons', question: 'What are assigned lessons?', answer: 'Your coach can assign specific lessons to your team. You\'ll see a notification banner in the Learn tab and a 📋 badge on assigned lessons in your learning path.' },
              { id: 'switch-sport', question: 'Can I switch sports?', answer: 'Yes! Use the sport switcher on the Home tab to change your active sport at any time. Your progress is tracked independently for each sport.' },
              { id: 'leaderboard', question: 'How does the leaderboard work?', answer: 'The leaderboard ranks all players by XP. Follow friends to see a Friends-only leaderboard. Your rank updates in real time as you and others earn XP.' },
            ]}
          />
        </motion.div>

        {/* Account */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          {!isPlayer && <ChangePasswordCard email={user?.email ?? ''} />}
          <Button
            onClick={async () => { if (signingOut) return; setSigningOut(true); try { await signOut(); } finally { setSigningOut(false); } }}
            disabled={signingOut}
            aria-busy={signingOut}
            variant="outline"
            className="w-full h-12 text-base font-display font-bold rounded-xl border-2 text-destructive hover:bg-destructive/10"
          >
            {signingOut ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing you out…</>) : 'Sign Out'}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            {user?.email}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
